import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global, ExecuteRunner } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo, ValueChangedEvent } from "@shared/type/CommonType";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import SnButton from "@shared/components/SnButton";

interface Props {
  Menuprops: menuInfo;
}

const SN_MTL_BAS_REG_002 = forwardRef(({ Menuprops }: Props, ref) => {
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
  });

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const { userInfo } = global(); // Login user 정보
  const searchRef = useRef<any>(null);

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
  });
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  // 특정항목 조회조건 바뀌면 재조회
  const onSearchPanelValueChanged = (event: ValueChangedEvent) => {
    const { colId } = event;

    switch (colId) {
      case "cd_biz":
      case "yy":
      case "cd_wh":
        onSearch(null);
        break;
    }
  };

  //#region Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onDelete,
    onSave,
    onExecute,
  }));

  //#region onSearch
  const onSearch = async (searchData: any) => {
    const requiredValuesCheckSearch = searchRef.current.requiredValuesCheck();

    if (!requiredValuesCheckSearch) {
      // 초기화
      setDataSources({
        grid1: [],
      });
      return false;
    }

    const modifiedRows = getGridCurrent("grid1").getChange();

    if (modifiedRows.modifiedRowcount > 0) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) {
          return;
        }
      } else if (confirm === undefined) {
        return;
      }
    }

    try {
      const searchDataToUse = searchData || searchRef.current.getFormdata();
      searchDataToUse.yy = searchDataToUse.yy.slice(0, 4);

      const params = {
        id: "sn_mtl_bas_reg_002_01",
        params: searchDataToUse,
      };

      getGridCurrent("grid1").resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
      });
    } catch (error: any) {}
  };
  //#endregion

  //#region onDelete
  const onDelete = async () => {
    if (getGridCurrent("grid1").getDisplayedRowCount() < 1) {
      return;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");

    if (!confirm) {
      return; // 사용자가 아니오를 선택하면 함수 종료
    }

    getGridCurrent("grid1").deleteAllRows();

    const realDeletedRows = getGridCurrent("grid1").getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      // 실제로 삭제된 행이 없으면 변경 사항을 적용하고 반환합니다.
      getGridCurrent("grid1").resetUpdate();
      return;
    }

    return await onSave();
  };
  //endregion

  //#region onSave
  const onSave = async () => {
    try {
      getGridCurrent("grid1").stopEditing();

      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      let hasInvalid = false;
      // 단순 불러오기만 해서 저장할 수 있으므로
      getGridCurrent("grid1").forEachNode((node: any) => {
        if (!node.data.cd_spare) {
          hasInvalid = true;
        }
      });

      const baseinventoryChanges = getGridCurrent("grid1").getChange();

      // 단순 불러오기만 해서 저장할 수 있으므로
      if (!hasInvalid) {
        if (baseinventoryChanges.modifiedRowcount === 0) {
          await showDialog("alter", "변경된 내용이 없습니다.");
          return false;
        }
      }

      // 중복체크
      const cdItem: any[] = [];
      getGridCurrent("grid1").forEachNode((node: any) => {
        cdItem.push(node.data.cd_item);
      });

      const dupItem = cdItem.filter(
        (item, idx) => cdItem.indexOf(item) !== idx
      );

      if (dupItem.length > 0) {
        await showDialog(
          "alter",
          `중복된 품목코드가 존재합니다: [${[...new Set(dupItem)].join(", ")}]`
        );
        return;
      }

      const searchInfo = searchRef.current.getFormdata();
      const cdBiz = searchInfo.cd_biz;
      const cdWh = searchInfo?.cd_wh;
      const yy = searchInfo?.yy.slice(0, 4);
      const result: any[] = [];

      // 품목 불러온 건 있으면 필수값 셋팅
      getGridCurrent("grid1").forEachNode((node: any) => {
        if (!node.data.cd_spare) {
          result.push({
            ...node.data,
            cd_biz: cdBiz,
            cd_wh: cdWh,
            yy: yy,
            cd_spare: "*",
          });
        }
      });

      // result(insert)로 들어갈 항목인데 update,delete로 잡히는 건 제거
      const resultItemKeys = new Set(result.map((item) => item.cd_item));

      const updatedData = (baseinventoryChanges.update || []).filter(
        (updItem: any) => !resultItemKeys.has(updItem.cd_item)
      );

      const deletedData = (baseinventoryChanges.delete || []).filter(
        (updItem: any) => !resultItemKeys.has(updItem.cd_item)
      );

      const baseinventoryData = {
        insert: [...(baseinventoryChanges.insert || []), ...(result || [])],
        update: updatedData,
        delete: deletedData,
        modifiedRowcount:
          result.length +
          baseinventoryChanges.insert.length +
          updatedData.length +
          deletedData.length,
      };
      // 요청 데이터 구성
      const SaveData = {
        baseinventory: baseinventoryData,
      };

      // 저장 API 호출
      const ok = await fnSave("SN_MTL_BAS_REG_002_SAVE_01", SaveData);
      if (!ok) return false;

      // 성공적으로 저장된 후 초기화
      getGridCurrent("grid1").resetUpdate();

      await onSearch(null);
      return true;
    } catch (error: any) {
      return false;
    }
  };
  //#endregion

  //#region onExecute
  const onExecute = async (id: string) => {
    const requiredValuesCheckSearch = searchRef.current.requiredValuesCheck();
    if (!requiredValuesCheckSearch) return false;

    const confirm = await showDialog("confirm", "전개하시겠습니까?");

    if (!confirm) {
      return; // 사용자가 아니오를 선택하면 함수 종료
    }

    const searchInfo = searchRef.current.getFormdata();
    searchInfo.yy = searchInfo.yy.slice(0, 4);

    switch (id) {
      case "All": // 품목전개
        {
          if (getGridCurrent("grid1").getDisplayedRowCount() > 0) {
            getGridCurrent("grid1").deleteAllRows();
          }
          const params = {
            id: "sn_mtl_bas_reg_002_02",
            params: searchInfo,
          };

          const data: any = await fnGetDataSet(params);
          setDataSources({
            grid1: data[0],
          });
        }
        break;
      case "Add": // 추가품목전개
        {
          const params = {
            id: "sn_mtl_bas_reg_002_03",
            params: searchInfo,
          };

          const data: any = await fnGetDataSet(params);

          setDataSources({
            grid1: [...dataSources.grid1, ...data[0]],
          });
        }
        break;
    }
  };
  //#endregion

  //#region GridEvents
  const onBeforeAddRow = (): any => {
    try {
      const searchInfo = searchRef.current.getFormdata();
      const updatedRow = {
        cd_firm: userInfo?.cd_firm,
        cd_biz: searchInfo?.cd_biz,
        cd_wh: searchInfo?.cd_wh,
        yy: searchInfo?.yy.slice(0, 4),
        cd_spare: "*",
        qt_good: 0,
        qt_bad: 0,
        qt_rcl: 0,
        qt: 0,
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onCellValueChanged = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    switch (colId) {
      case "qt_good":
      case "qt_bad":
      case "qt_rcl":
        rowNode.setDataValue(
          "qt",
          rowData.qt_good + rowData.qt_bad + rowData.qt_rcl,
          "noEvent"
        );
        break;
    }
  };

  const onGridBtnsClick = async (id: string) => {
    switch (id) {
      case "addRow":
        const requiredValuesCheckSearch =
          searchRef.current.requiredValuesCheck();
        if (!requiredValuesCheckSearch) return false;
        getGridCurrent("grid1").addRow();
        break;
      case "deleteRow":
        if (getGridCurrent("grid1").getDisplayedRowCount() > 0) {
          getGridCurrent("grid1").deleteSelectedRows();
        }
        break;
    }
  };
  //#endregion

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onCellValueChanged: onCellValueChanged,
          onBtnAddClick: onGridBtnsClick,
          onBtnDeleteClick: onGridBtnsClick,
          onBeforeAddRow: onBeforeAddRow,
        },
      },
    }),
    [columns, dataSources]
  );
  //#endregion

  //#region UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
          onValueChanged={onSearchPanelValueChanged}
        ></SearchPanel>
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnGrid
          header={[
            <div style={{ paddingLeft: "80px" }}>
              <SnButton
                text={"품목전개"}
                onClick={() => {
                  ExecuteRunner.run("All");
                }}
              />
              <SnButton
                text={"추가품목전개"}
                onClick={() => {
                  ExecuteRunner.run("Add");
                }}
              />
            </div>,
          ]}
          ref={gridRefs.current["grid1"]} // Grid Name
          columnConfig={grids.grid1.columns} // 컬럼 설정 전달
          rowData={grids.grid1.dataSource} // Datasource
          etcProps={{ rowSelection: "multiple", title: "상세정보" }}
          events={grids.grid1.events}
        />
      </div>
    </div>
  );
  //#endregion
});

export default SN_MTL_BAS_REG_002;
